import { useEffect, useState } from "react";
import { EvidenceRequestAnswerForm } from "../components/EvidenceRequestAnswer";
import { CircleCheck, Circle, Timer } from "lucide-react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { api } from "../api/client";
import { theme, card, floatingSheet, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { controlRoute } from "../utils/controlRoute";
import { useNavModel } from "../nav/areas";
import { useNavCountsContext } from "../nav/NavCountsContext";
import type { StatutoryClock } from "../nav/NavCountsContext";
import type { SetupChecklist } from "../nav/types";
import { dateInputToIso, todayInput } from "../lib/dateOnly";
import { controlRefWithStandard } from "../lib/controlRef";

interface ActionItem {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  severity: "high" | "medium" | "low";
  dueAt: string | null;
  // Set when the action concerns one control, so the row can link to it.
  controlId?: string;
  frameworkId?: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  finding:          "Auditor Findings",
  evidence_request: "Evidence Requests",
  overdue_event:    "Overdue Reviews",
  overdue_check:    "Overdue Compliance Checks",
  dsr:              "Data Subject Requests",
  connection_failing: "Connections",
  vuln_sla_breached: "Security update deadline missed",
  policy_campaign_overdue: "Policy acknowledgement overdue",
  training_assignment_overdue: "Training overdue",
  training_phishing_proposal: "Phishing training proposal",
  training_competence_proposal: "Training proposed from a competence gap",
  certification_countdown_behind: "Audit readiness task behind",
  control_attestation_lapsed: "Control owner attestation overdue",
  control_attestation_not_operating: "Control said not to be operating",
  evidence_validity_due: "Evidence due for renewal",
  evidence_validity_lapsed: "Evidence out of date",
  policy_exception_expiring: "Policy exception expiring",
  policy_exception_renewal_limit: "Policy exception renewed too often",
  // typeLabel() drops a trailing "s", so these labels must not end in one.
  supplier_renewal_reassess: "Supplier re-assessment before renewal",
  supplier_renewal_locked: "Agreement renewed with no decision",
  supplier_renewal_lapsed: "Agreement ending with no renewal decision",
  supplier_compromise_open: "Supplier may be compromised",
  supplier_assurance_pending: "Assurance report to confirm",
  supplier_cuec_unmapped: "Supplier control you must operate, not mapped",
  supplier_advisory_named: "Advisory naming a supplier",
  policy_shortfall_overdue: "Policy review overdue after a lesson learned",
  policy_shortfall_new_version: "New policy version to check against a lesson learned",
  change_emergency_review: "Emergency change to close",
  change_past_planned: "Change past its planned date",
  programme_behind: "Programme behind schedule",
  bia_rto_gap: "Recovery time not achievable",
  // typeLabel() drops a trailing "s", so this label must not end in one.
  bia_recovery_commitment: "Supplier or restore test short of the recovery objective",
  ce_patching_contradiction: "Cyber Essentials answer contradicted",
  ai_high_risk_no_dpia: "High-risk AI system without an impact assessment",
  ai_no_processing_record: "AI system missing from the record of processing",
  data_secrets_found: "Passwords or keys found",
  data_cards_found: "Card numbers found",
  leaver_incomplete: "Leaver checklist incomplete",
  asset_unsupported: "Asset out of vendor support",
  standard_superseded: "Standard changed",
  law_changed: "Law changed",
  law_text_changed: "Law text changed",
  policy_adoption: "Policy awaiting adoption",
  asset_classification_proposed: "Suggested asset classification",
};

// Abbreviations a type key carries, written as people write them.
const TYPE_ACRONYMS: Record<string, string> = {
  bia: "BIA", rto: "RTO", rpo: "RPO", ce: "Cyber Essentials", dpia: "DPIA", dsr: "Data subject request", ai: "AI",
  oes: "OES", dora: "DORA", sla: "deadline", mfa: "MFA", cve: "CVE", kev: "KEV", ropa: "RoPA", jcsc: "JCSC",
  vuln: "Vulnerability", ms: "Management system", tprm: "Supplier", soa: "SOA", ot: "OT", siem: "SIEM", dpa: "DPA", pir: "Post-incident review",
};

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

/**
 * One ranked list: most severe first, then the nearest due date, then undated.
 * Grouping by type answered "what kinds of thing are there"; the question a
 * person arrives with is "what do I do first".
 */
export function rankActions<T extends { severity: string; dueAt: string | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const s = (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3);
    if (s !== 0) return s;
    if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
    if (a.dueAt) return -1;
    if (b.dueAt) return 1;
    return 0;
  });
}

function typeLabel(type: string): string {
  const label = TYPE_LABELS[type];
  if (label) return label.replace(/s$/, "");
  const words = type.split("_").map((w) => TYPE_ACRONYMS[w] ?? w).join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ── Next statutory deadline and setup steps, above the list ───────────────

function DeadlineCard({ clock }: { clock: StatutoryClock }) {
  const navigate = usePortalNavigate();
  const tone = clock.urgency === "overdue"
    ? { bg: "var(--c-dangerBg)", text: "var(--c-dangerText)", label: "Overdue" }
    : clock.urgency === "due_soon"
      ? { bg: "var(--c-warningBg)", text: "var(--c-warningText)", label: "Due soon" }
      : { bg: "var(--c-accentBg)", text: "var(--c-accentText)", label: "Next deadline" };
  return (
    <button
      type="button"
      onClick={() => navigate(clock.href)}
      style={{ ...card, display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", cursor: "pointer", marginBottom: 16, fontFamily: theme.font }}
    >
      <span style={{ display: "flex", color: tone.text }}><Timer size={18} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: theme.color.heading }}>{clock.label}</span>
        <span style={{ display: "block", fontSize: 12.5, color: theme.color.subtle, marginTop: 2 }}>{clock.detail} - due {fmtDate(clock.dueAt)}</span>
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", background: tone.bg, color: tone.text, borderRadius: 3, padding: "3px 8px", flexShrink: 0 }}>
        {tone.label}
      </span>
    </button>
  );
}

function SetupCard({ setup }: { setup: SetupChecklist }) {
  const navigate = usePortalNavigate();
  const done = setup.items.filter((i) => i.done).length;
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: theme.color.heading }}>{setup.title ?? "Get set up"}</span>
        <span style={{ fontSize: 12, color: theme.color.subtle }}>{done} of {setup.items.length} done</span>
      </div>
      {setup.items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => navigate(item.to)}
          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 16px", border: "none", borderTop: "1px solid var(--c-border)", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: theme.font }}
        >
          <span style={{ display: "flex", color: item.done ? "var(--c-success)" : "var(--c-faint)" }}>
            {item.done ? <CircleCheck size={16} /> : <Circle size={16} />}
          </span>
          <span style={{ fontSize: 13, color: item.done ? theme.color.subtle : theme.color.heading, textDecoration: item.done ? "line-through" : "none" }}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

// One-click completion for the row's tick box. Types that need more input
// (a reschedule date, a DSR outcome) are resolved from the drawer instead.
function quickResolve(item: ActionItem): (() => Promise<unknown>) | null {
  switch (item.type) {
    case "finding":          return () => api.patch(`/findings/${item.id}/status`, { status: "resolved" });
    case "overdue_check":    return () => api.post("/schedule/compliance-checks/done", { checkKey: item.id.replace(/^check:/, "") });
    default:                 return null;
  }
}

const SEVERITY_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  high:   { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)",  label: "High" },
  medium: { bg: "var(--c-warningBg)", text: "var(--c-warningText)", label: "Medium" },
  low:    { bg: "var(--c-accentBg)",  text: "var(--c-accentText)",  label: "Low" },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Drawer — type-specific resolution UI
function ResolutionDrawer({
  item,
  onClose,
  onResolved,
}: {
  item: ActionItem;
  onClose: () => void;
  onResolved: (id: string) => void;
}) {
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  // for reschedule
  const today = todayInput();
  const [rescheduleDate, setRescheduleDate] = useState(today);

  const sev = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.low;

  async function run(action: () => Promise<void>) {
    setSaving(true);
    setError(null);
    try {
      await action();
      onResolved(item.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setSaving(false);
    }
  }

  function resolveFinding(status: "acknowledged" | "resolved") {
    return run(() => api.patch(`/findings/${item.id}/status`, { status }));
  }

  function resolveEvidenceRequest(status: "acknowledged") {
    return run(() => api.patch(`/findings/evidence-requests/${item.id}`, { status }));
  }

  function completeDsr() {
    return run(() => api.patch(`/gdpr/dsr/${item.id}`, { status: "completed" }));
  }

  function rescheduleEvent() {
    if (!rescheduleDate) return;
    return run(() => api.patch(`/schedule/${item.id}`, { eventDate: dateInputToIso(rescheduleDate) }));
  }

  function markCheckDone() {
    const checkKey = item.id.replace(/^check:/, "");
    return run(() => api.post("/schedule/compliance-checks/done", { checkKey }));
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 200,
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 400, maxWidth: "90vw",
        ...floatingSheet,
        zIndex: 201,
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid var(--c-line)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
              textTransform: "uppercase",
              background: sev.bg, color: sev.text,
              borderRadius: 3, padding: "3px 8px",
            }}>
              {sev.label}
            </span>
            <span style={{ fontSize: 12, color: "var(--c-subtle)", fontWeight: 600 }}>
              {typeLabel(item.type)}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, lineHeight: 1 }}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--c-faint)" strokeWidth="2" strokeLinecap="round">
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: "var(--c-heading)", margin: "0 0 8px" }}>
            {item.title}
          </h2>
          <p style={{ fontSize: 13, color: "var(--c-subtle)", lineHeight: 1.6, margin: "0 0 16px" }}>
            {item.description}
          </p>
          {item.dueAt && (
            <div style={{ fontSize: 12, color: "var(--c-faint)", marginBottom: 20 }}>
              Due {fmtDate(item.dueAt)}
            </div>
          )}

          {/* Type-specific resolution controls */}
          {item.type === "finding" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--c-muted)", margin: 0, marginBottom: 4 }}>
                Respond to this auditor finding:
              </p>
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={() => resolveFinding("resolved")}
              >
                Mark Resolved
              </button>
              <button
                style={{ ...btnSecondary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={() => resolveFinding("acknowledged")}
              >
                Acknowledge
              </button>
            </div>
          )}

          {item.type === "evidence_request" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--c-muted)", margin: 0, marginBottom: 4 }}>
                Answer it with the documents that show it: the auditor sees exactly the versions you send.
              </p>
              <EvidenceRequestAnswerForm
                requestId={item.id}
                onCancel={onClose}
                onAnswered={() => onResolved(item.id)}
              />
              <button
                style={{ ...btnSecondary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={() => resolveEvidenceRequest("acknowledged")}
              >
                Acknowledge
              </button>
            </div>
          )}

          {item.type === "dsr" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--c-muted)", margin: 0, marginBottom: 4 }}>
                Mark this data subject request as fulfilled:
              </p>
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={completeDsr}
              >
                Mark Completed
              </button>
            </div>
          )}

          {item.type === "overdue_check" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--c-muted)", margin: 0, marginBottom: 4 }}>
                Confirm this check has been completed and reset its next due date:
              </p>
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={markCheckDone}
              >
                Mark as Done
              </button>
            </div>
          )}

          {item.type === "overdue_event" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--c-muted)", margin: 0, marginBottom: 4 }}>
                Reschedule this event to a future date:
              </p>
              <input
                type="date"
                min={today}
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                style={inputStyle}
              />
              <button
                style={{ ...btnPrimary, opacity: saving || !rescheduleDate ? 0.6 : 1 }}
                disabled={saving || !rescheduleDate}
                onClick={rescheduleEvent}
              >
                Reschedule
              </button>
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 14, fontSize: 12,
              color: "var(--c-dangerText)",
              background: "var(--c-dangerBg)",
              borderRadius: 3, padding: "8px 12px",
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer — open full page link */}
        <div style={{
          borderTop: "1px solid var(--c-border)",
          padding: "14px 20px",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          {item.controlId && (
            <button
              onClick={() => { onClose(); navigate(controlRoute(item.controlId!, item.frameworkId)); }}
              style={{ ...btnSecondary, width: "100%", textAlign: "center" }}
            >
              Open {controlRefWithStandard(item.controlId) ?? "the obligation"}
            </button>
          )}
          <button
            onClick={() => { onClose(); navigate(item.href); }}
            style={{
              ...btnSecondary,
              width: "100%",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            Open full page
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M4 2h6v6M10 2L2 10" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

export function ActionsPage() {
  const [items, setItems]       = useState<ActionItem[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [selected, setSelected] = useState<ActionItem | null>(null);
  // Rows ticked off in this session stay visible, struck through, until the page reloads.
  const [done, setDone]         = useState<Set<string>>(new Set());
  const [ticking, setTicking]   = useState<string | null>(null);
  const [tickError, setTickError] = useState<Record<string, string>>({});
  const { counts, refresh: refreshNavCounts } = useNavCountsContext();

  useEffect(() => {
    api.get<{ total: number; items: ActionItem[] }>("/actions")
      .then((d) => { setTotal(d.total); setItems(d.items); })
      .catch((e) => setError(e?.message ?? "Failed to load actions"))
      .finally(() => setLoading(false));
  }, []);

  function handleResolved(id: string) {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setTotal((prev) => Math.max(0, prev - 1));
    setSelected(null);
    refreshNavCounts();
  }

  async function tick(item: ActionItem) {
    const action = quickResolve(item);
    if (!action || done.has(item.id) || ticking) return;
    setTicking(item.id);
    setTickError((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    try {
      await action();
      setDone((prev) => new Set(prev).add(item.id));
      setTotal((prev) => Math.max(0, prev - 1));
      refreshNavCounts();
    } catch (e: unknown) {
      setTickError((prev) => ({ ...prev, [item.id]: e instanceof Error ? e.message : "Request failed" }));
    } finally {
      setTicking(null);
    }
  }

  const ranked = rankActions(items);
  const { model } = useNavModel();
  const clock = counts?.role === "client" ? counts.deadline : null;
  const setup = model?.setup;

  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: theme.color.heading, margin: 0, marginBottom: 4 }}>
          What's next
        </h1>
        <p style={{ fontSize: 13, color: theme.color.subtle, margin: 0 }}>
          Everything that needs doing, most urgent first.
        </p>
      </div>

      {clock && <DeadlineCard clock={clock} />}
      {setup && <SetupCard setup={setup} />}

      {loading && <LoadingBlock />}

      {error && (
        <div style={{ ...card, color: theme.color.dangerText, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loading && !error && total === 0 && (
        <div style={{ ...card, textAlign: "center", padding: "40px 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: "var(--c-success)" }}><CircleCheck size={32} /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.successText, marginBottom: 4 }}>
            All clear
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle }}>
            No outstanding actions — you're up to date.
          </div>
        </div>
      )}

      {!loading && !error && total > 0 && (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
                {ranked.map((item, idx) => {
                  const sev    = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.low;
                  const isLast = idx === ranked.length - 1;
                  const isSelected = selected?.id === item.id;
                  const isDone = done.has(item.id);
                  const tickable = quickResolve(item) !== null;
                  const rowError = tickError[item.id];
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "0 0 0 16px",
                        borderBottom: isLast ? "none" : `1px solid var(--c-border)`,
                        opacity: isDone ? 0.55 : 1,
                        transition: "opacity 0.2s",
                      }}
                    >
                      {/* Tick to resolve */}
                      <label
                        title={tickable ? (isDone ? "Done" : "Tick to mark as resolved") : "Open this action to resolve it"}
                        style={{ display: "flex", alignItems: "center", paddingTop: 15, cursor: tickable && !isDone ? "pointer" : "default", flexShrink: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={isDone}
                          disabled={!tickable || isDone || ticking === item.id}
                          onChange={() => tick(item)}
                          aria-label={`Mark "${item.title}" as resolved`}
                          style={{ width: 15, height: 15, accentColor: "var(--c-success)", cursor: tickable && !isDone ? "pointer" : "default" }}
                        />
                      </label>

                      <button
                        onClick={() => setSelected(item)}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          padding: "14px 20px 14px 4px",
                          background: isSelected ? "var(--c-accentBg)" : "transparent",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "left",
                          flex: 1,
                          minWidth: 0,
                          transition: "background 0.12s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) (e.currentTarget as HTMLElement).style.background = "var(--c-panelAlt)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent";
                        }}
                      >
                        {/* Severity badge */}
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          background: isDone ? "var(--c-successBg)" : sev.bg,
                          color: isDone ? "var(--c-successText)" : sev.text,
                          borderRadius: 3,
                          padding: "3px 7px",
                          flexShrink: 0,
                          marginTop: 1,
                        }}>
                          {isDone ? "Done" : sev.label}
                        </span>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 2, textDecoration: isDone ? "line-through" : "none" }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                            {item.description}
                          </div>
                          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                            {typeLabel(item.type)}{item.dueAt ? ` - due ${fmtDate(item.dueAt)}` : ""}
                          </div>
                          {rowError && (
                            <div style={{ fontSize: 11, color: "var(--c-dangerText)", marginTop: 4 }}>{rowError}</div>
                          )}
                        </div>

                        {/* Chevron */}
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--c-faint)" strokeWidth="1.6" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 3 }}>
                          <path d="M5 3l4 4-4 4" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
          </div>
        </div>
      )}

      {selected && (
        <ResolutionDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
}
